<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, Ref, ref, watch} from "vue";
import backendCall from "@/services/AxiosService";
import DataTable, {DataTablePageEvent, DataTableSortEvent} from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import { useAuthStore } from '@/stores/authStore';
import { PERMISSIONS } from '@/constants/permissions';
import ManageBuildPositionsDialog from '@/components/powerpacks/ManageBuildPositionsDialog.vue';
import { useTimeTrackingStore } from '@/stores/timeTrackingStore';
import TimeTrackingService from '@/services/TimeTrackingService';
import { useToast } from 'primevue/usetoast';

const gensetBuilds: Ref<any[]> = ref([]);

const authStore = useAuthStore();
const timeTrackingStore = useTimeTrackingStore();
const toast = useToast();
const manageDialogVisible = ref(false);
const selectedBuild = ref<any | null>(null);
const totalRecords = ref(0);
const loading = ref(false);
const pendingSignInBuildId = ref<number | null>(null);

// PrimeVue expects these specific properties for lazy loading
const lazyParams = ref({
  first: 0,
  rows: 10,
  page: 0,
  sortField: 'build_id',
  sortOrder: -1
});

const fetchBuilds = async () => {
  try {
    loading.value = true;

    const params = {
      page: lazyParams.value.page + 1, // Convert to 1-based for backend
      rows: lazyParams.value.rows,
      sortField: lazyParams.value.sortField,
      sortOrder: lazyParams.value.sortOrder
    };

    const response = await backendCall.get('power-pack/builds', { params });
    const data: {content: any[], totalRecords: number} = response.data;

    if (Array.isArray(data.content)) {
      gensetBuilds.value = data.content.map((item) => ({
        ...item,
        positions_count: item.positions_count !== null ? Number(item.positions_count) : 0
      }));
      totalRecords.value = data.totalRecords;
      if (selectedBuild.value) {
        const updated = gensetBuilds.value.find((entry) => entry.build_id === selectedBuild.value.build_id);
        if (updated) {
          selectedBuild.value = updated;
        }
      }
    }
  } catch (error) {
    console.error('There was a problem with the axios operation:', error);
  } finally {
    loading.value = false;
  }
};

const openManageDialog = (build: any) => {
  selectedBuild.value = build;
  manageDialogVisible.value = true;
};

const handlePositionsChanged = async () => {
  await fetchBuilds();
};

const onPage = (event: DataTablePageEvent) => {
  lazyParams.value = {
    ...lazyParams.value,
    first: event.first,
    rows: event.rows,
    page: Math.floor(event.first / event.rows)
  };
  void fetchBuilds();
};

const onSort = (event: DataTableSortEvent) => {
  lazyParams.value = {
    ...lazyParams.value,
    sortField: event.sortField as string,
    sortOrder: event.sortOrder as number
  };
  void fetchBuilds();
};

// Get user's active build time records
const userActiveBuildRecords = computed(() => {
  return timeTrackingStore.activeTimeRecords.filter(record => {
    return (record.type === 'Build' || record.recordDetails?.buildId) &&
           record.isStartedByCurrentUser;
  });
});

// Check if current user is tracking time on a specific build
const isUserTrackingBuild = (buildId: number) => {
  return userActiveBuildRecords.value.some(record => {
    const recordBuildId = record.recordDetails?.buildId;
    return Number(recordBuildId) === Number(buildId);
  });
};

// Get elapsed time for a specific build
const getBuildElapsedSeconds = (buildId: number) => {
  const record = userActiveBuildRecords.value.find(r => {
    const recordBuildId = r.recordDetails?.buildId;
    return Number(recordBuildId) === Number(buildId);
  });
  return record?.elapsedSeconds || 0;
};

// Format seconds to HH:MM:SS
const formatElapsedTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Sign in to a build
const signInToBuild = async (buildId: number) => {
  try {
    pendingSignInBuildId.value = buildId;
    const response = await TimeTrackingService.startBuildTime(buildId);

    if (response?.success === false && response?.error) {
      if (response.error.code === 'ACTIVE_TIME_EXISTS') {
        toast.add({
          severity: 'warn',
          summary: 'Already Tracking Time',
          detail: response.error.message || 'You already have an active time record on another build',
          life: 5000
        });
      } else {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: response.error.message || 'Failed to sign in',
          life: 3000
        });
      }
      return;
    }

    toast.add({
      severity: 'success',
      summary: 'Signed In',
      detail: 'You are now tracking time on this build',
      life: 3000
    });

    await timeTrackingStore.fetchActiveTimeRecords();
  } catch (error: any) {
    console.error('Error signing in to build:', error);
    const errorMessage = error.response?.data?.error?.message || 'Failed to sign in';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 3000
    });
  } finally {
    pendingSignInBuildId.value = null;
  }
};

// Sign out from a build
const signOutFromBuild = async (buildId: number) => {
  try {
    pendingSignInBuildId.value = buildId;
    await TimeTrackingService.endBuildTime(buildId);

    toast.add({
      severity: 'success',
      summary: 'Signed Out',
      detail: 'Time tracking stopped',
      life: 3000
    });

    await timeTrackingStore.fetchActiveTimeRecords();
  } catch (error: any) {
    console.error('Error signing out from build:', error);
    const errorMessage = error.response?.data?.error?.message || 'Failed to sign out';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 3000
    });
  } finally {
    pendingSignInBuildId.value = null;
  }
};

// Get genset number for a build record
const getGensetNumberForRecord = (record: any) => {
  const buildId = record.recordDetails?.buildId;
  const build = gensetBuilds.value.find(b => Number(b.build_id) === Number(buildId));
  return build?.genset_number || record.recordDetails?.gensetNumber || `Build #${buildId}`;
};

onMounted(async() => {
  await Promise.all([
    fetchBuilds(),
    timeTrackingStore.fetchActiveTimeRecords()
  ]);
  // Always start timer if store has ANY active records (timer updates elapsedSeconds)
  if (timeTrackingStore.activeTimeRecords.length > 0) {
    timeTrackingStore.startTimer();
  }
});

onBeforeUnmount(() => {
  timeTrackingStore.stopTimer();
});

// Start/stop timer based on whether store has active records
watch(
  () => timeTrackingStore.activeTimeRecords.length,
  (length) => {
    if (length > 0) {
      timeTrackingStore.startTimer();
    } else {
      timeTrackingStore.stopTimer();
    }
  },
  { immediate: true }
);

watch(
  () => manageDialogVisible.value,
  (visible) => {
    if (!visible) {
      selectedBuild.value = null;
    }
  }
);

</script>

<template>
  <div class="bg-surface-0 dark:bg-surface-950 px-6 py-8 md:px-12 lg:px-20">
    <div class="flex flex-col md:items-center md:justify-center md:flex-row">
      <div class="font-medium text-3xl text-surface-900 dark:text-surface-0">
        Genset Builds
      </div>
    </div>
  </div>

  <!-- Active Sessions Panel -->
  <!--  <div v-if="userActiveBuildRecords.length > 0" class="mx-6 md:mx-12 lg:mx-20 mb-4">-->
  <!--    <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">-->
  <!--      <h3 class="text-lg font-semibold text-green-800 dark:text-green-200 mb-3 flex items-center gap-2">-->
  <!--        <i class="pi pi-clock"></i>-->
  <!--        Your Active Sessions-->
  <!--      </h3>-->
  <!--      <div class="space-y-2">-->
  <!--        <div v-for="record in userActiveBuildRecords" :key="record.id"-->
  <!--             class="flex items-center justify-between bg-white dark:bg-surface-800 rounded-lg p-3 shadow-sm">-->
  <!--          <div class="flex items-center gap-3">-->
  <!--            <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white">-->
  <!--              <i class="pi pi-wrench text-sm"></i>-->
  <!--            </span>-->
  <!--            <div>-->
  <!--              <span class="font-medium text-surface-900 dark:text-surface-0">-->
  <!--                {{ getGensetNumberForRecord(record) }}-->
  <!--              </span>-->
  <!--              <span class="text-sm text-surface-500 ml-2">-->
  <!--                (Build #{{ record.recordDetails?.buildId }})-->
  <!--              </span>-->
  <!--            </div>-->
  <!--          </div>-->
  <!--          <div class="flex items-center gap-3">-->
  <!--            <span class="font-mono text-lg font-bold text-green-600 dark:text-green-400">-->
  <!--              {{ formatElapsedTime(record.elapsedSeconds || 0) }}-->
  <!--            </span>-->
  <!--            <Button-->
  <!--              label="Sign Out"-->
  <!--              icon="pi pi-stop"-->
  <!--              severity="danger"-->
  <!--              size="small"-->
  <!--              :loading="pendingSignInBuildId === Number(record.recordDetails?.buildId)"-->
  <!--              @click="signOutFromBuild(Number(record.recordDetails?.buildId))"-->
  <!--            />-->
  <!--          </div>-->
  <!--        </div>-->
  <!--      </div>-->
  <!--    </div>-->
  <!--  </div>-->

  <DataTable
    :value="gensetBuilds"
    :lazy="true"
    :total-records="totalRecords"
    :loading="loading"
    data-key="build_id"
    :rows="lazyParams.rows"
    :first="lazyParams.first"
    :rows-per-page-options="[10, 20, 50, 100]"
    :sort-order="lazyParams.sortOrder"
    :sort-field="lazyParams.sortField"
    paginator
    striped-rows
    removable-sort
    table-style="min-width: 50rem"
    @page="onPage"
    @sort="onSort"
  >
    <Column
      field="build_id"
      header="ID"
      sortable
    />
    <Column
      field="genset_number"
      header="Genset"
      sortable
    />
    <Column
      field="track_id"
      header="Track"
      sortable
    />
    <Column
      field="positions_count"
      header="Units"
      sortable
    >
      <template #body="slotProps">
        {{ slotProps.data.positions_count ?? 0 }}
      </template>
    </Column>
    <Column
      field="call_in_time"
      header="Call In Time"
      sortable
    >
      <template #body="slotProps">
        {{ slotProps.data.call_in_time ? new Date(slotProps.data.call_in_time).toLocaleString("en-US", {hourCycle: "h23"}) : '' }}
      </template>
    </Column>
    <Column
      field="start_time"
      header="Start Time"
      sortable
    >
      <template #body="slotProps">
        {{ slotProps.data.start_time ? new Date(slotProps.data.start_time).toLocaleString("en-US", {hourCycle: "h23"}) : '' }}
      </template>
    </Column>
    <Column header="Entered By">
      <template #body="slotProps">
        {{ slotProps.data.entered_by_first_name + " " + slotProps.data.entered_by_last_name }}
      </template>
    </Column>
    <Column
      v-if="authStore.hasPermission(PERMISSIONS.POWER_PACKS_READ)"
      header="Actions"
    >
      <template #body="slotProps">
        <div class="flex items-center gap-2">
          <!-- Sign In/Out Button -->
          <template v-if="isUserTrackingBuild(slotProps.data.build_id)">
            <span class="font-mono text-sm text-green-600 dark:text-green-400 font-bold">
              {{ formatElapsedTime(getBuildElapsedSeconds(slotProps.data.build_id)) }}
            </span>
            <Button
              label="Out"
              icon="pi pi-stop"
              severity="danger"
              size="small"
              :loading="pendingSignInBuildId === slotProps.data.build_id"
              @click="signOutFromBuild(slotProps.data.build_id)"
            />
          </template>
          <Button
            v-else
            label="Sign In"
            icon="pi pi-play"
            severity="success"
            size="small"
            :loading="pendingSignInBuildId === slotProps.data.build_id"
            @click="signInToBuild(slotProps.data.build_id)"
          />
          <!-- Manage Button -->
          <Button
            label="Manage"
            icon="pi pi-list"
            size="small"
            outlined
            @click="openManageDialog(slotProps.data)"
          />
        </div>
      </template>
    </Column>
  </DataTable>

  <ManageBuildPositionsDialog
    v-model:visible="manageDialogVisible"
    :build="selectedBuild"
    @positions-changed="handlePositionsChanged"
  />
</template>
